import { createRef, useEffect } from 'react';

import { Text } from '@opendoor/bricks/core';

import TwoColumnPageLayout from 'src/components/layouts/TwoColumnPageLayout';
import { showPrompt } from 'src/helpers/showPrompt';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getAnswers } from 'src/store/sellerFlow/selectors';
import { submitAndGoToNextPage } from 'src/store/ui';

import LoadingSection from '../common/loader/LoadingSection';
import AccountPage from './twoColumn/AccountPage';
import { BottomBar } from './twoColumn/BottomBar';
import FlowPageContent from './twoColumn/FlowPageContent';
import FlowPageHeader from './twoColumn/FlowPageHeader';
import Navigation from './twoColumn/Navigation';
import { ProgressIndicator } from './twoColumn/ProgressIndicator';

const TwoColumnFlowPage: React.FC = () => {
  const atEnd = useAppSelector((state) => state.flow_ng.atEnd);
  const titleRef = createRef<HTMLHeadingElement>();
  const page = useAppSelector((state) => state.ui.page);
  const answers = useAppSelector(getAnswers);
  const appDispatch = useAppDispatch();

  useEffect(() => {
    if (atEnd) {
      window.removeEventListener('beforeunload', showPrompt);
    } else {
      window.addEventListener('beforeunload', showPrompt);
    }

    // if we unload this page (because we sent them to an end of flow page
    // within reception) go ahead and remove the event
    return () => {
      window.removeEventListener('beforeunload', showPrompt);
    };
  }, [atEnd]);

  useEffect(() => {
    // Send focus to the title so users can tab into the form when navigating
    // pages instead of having the focus remain on the next/back button
    titleRef.current?.focus();
    // eslint-disable-next-line
  }, [page?.title]);

  if (!page) {
    return <LoadingSection />;
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    appDispatch(submitAndGoToNextPage());
  };

  return (
    <TwoColumnPageLayout
      slug={page.slug}
      leftColumnPageContent={
        <FlowPageHeader
          title={page.title ?? ''}
          subtitle={page.subtitle}
          subtitleElement={page.subtitleElement}
          ref={titleRef}
        />
      }
      rightColumnPageContent={
        <>
          <FlowPageContent page={page} answers={answers} />
          {/*<PhonePageNextButton /> */}
          {page.note && (
            <Text mt={6} color="neutrals90" fontSize="s00" textAlign={['left', null, 'center']}>
              {page.note}
            </Text>
          )}
        </>
      }
      navigationComponent={
        <BottomBar>
          <ProgressIndicator />
          <Navigation />
        </BottomBar>
      }
      formProps={{
        isForm: true,
        handleSubmit,
      }}
    />
  );
};

const TwoColumnFlowPageOrAccountPage = () => {
  const currentSlug = useAppSelector((state) => state.ui.page?.slug);
  const shortboardingVariant = useAppSelector((state) => state.experimentFlags.shortboarding);
  if (
    currentSlug == 'account' ||
    // checking shortboarding flag so when we remove/normalize we will remember to clean this up
    (shortboardingVariant == 'treatment' && currentSlug == 'account-sb')
  ) {
    return <AccountPage />;
  }
  return <TwoColumnFlowPage />;
};

export default TwoColumnFlowPageOrAccountPage;
